import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { getReasoningLevelLabel } from '../../utils/reasoningLevels';
import './ReasoningEffortPicker.css';

const ReasoningEffortPicker = ({
  disabled = false,
  level,
  levels = [],
  onLevelChange,
  popoverAlign = 'left',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const selectedValue = String(level || '').trim().toLowerCase();
  const sliderOptions = useMemo(
    () => [{ value: null, label: 'Auto' }, ...levels],
    [levels]
  );
  const selectedIndex = selectedValue
    ? levels.findIndex(({ value }) => value === selectedValue) + 1
    : 0;
  const normalizedSelectedIndex = selectedIndex > 0 ? selectedIndex : 0;
  const [sliderIndex, setSliderIndex] = useState(normalizedSelectedIndex);
  const sliderProgress = sliderOptions.length > 1
    ? (sliderIndex / (sliderOptions.length - 1)) * 100
    : 0;
  const displayedOption = sliderOptions[sliderIndex] || sliderOptions[0];
  const triggerLabel = isOpen
    ? 'Thinking Effort'
    : getReasoningLevelLabel(level);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (levels.length === 0 || disabled) setIsOpen(false);
  }, [disabled, levels.length]);

  useEffect(() => {
    setSliderIndex(normalizedSelectedIndex);
  }, [normalizedSelectedIndex]);

  if (levels.length === 0) return null;

  const togglePicker = () => {
    if (!isOpen) setSliderIndex(normalizedSelectedIndex);
    setIsOpen((open) => !open);
  };

  const handleSliderChange = (event) => {
    const nextIndex = Number(event.target.value);
    setSliderIndex(nextIndex);
    onLevelChange?.(sliderOptions[nextIndex]?.value || null);
  };

  const selectLevelAtIndex = (nextIndex) => {
    setSliderIndex(nextIndex);
    onLevelChange?.(sliderOptions[nextIndex]?.value || null);
  };

  return (
    <div ref={rootRef} className="relative flex-shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={togglePicker}
        aria-label={`Thinking effort: ${getReasoningLevelLabel(level)}`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        title="Thinking effort"
        className={`inline-flex h-9 items-center gap-1 rounded-full px-3 text-sm font-medium theme-text-secondary transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          isOpen
            ? 'bg-[var(--color-button-secondary)]'
            : 'hover:bg-[var(--color-button-secondary-hover)]'
        }`}
      >
        <span>{triggerLabel}</span>
        <ChevronDown
          size={14}
          strokeWidth={2}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Thinking effort"
          className={`arrow-reasoning-popover absolute bottom-full z-[1100] mb-2 theme-text-primary ${
            popoverAlign === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <div className="arrow-reasoning-popover-label">
            <span aria-live="polite">{displayedOption.label}</span>
          </div>

          <div className="arrow-reasoning-slider-shell">
            <div className="arrow-reasoning-slider-track" aria-hidden="true">
              <div
                className="arrow-reasoning-slider-progress"
                style={{ '--reasoning-slider-progress': `${sliderProgress}%` }}
              />
            </div>
            <div className="arrow-reasoning-slider-ticks">
              {sliderOptions.map((option, index) => (
                <button
                  type="button"
                  key={option.value || 'auto'}
                  onClick={() => selectLevelAtIndex(index)}
                  aria-label={`Set reasoning effort to ${option.label}`}
                  aria-current={index === sliderIndex ? 'true' : undefined}
                  className={`arrow-reasoning-slider-tick ${index <= sliderIndex ? 'is-active' : ''} ${index === sliderIndex ? 'is-current' : ''}`}
                  style={{ left: `${sliderOptions.length > 1 ? (index / (sliderOptions.length - 1)) * 100 : 0}%` }}
                />
              ))}
            </div>
            <input
              type="range"
              min="0"
              max={Math.max(sliderOptions.length - 1, 0)}
              step="1"
              value={sliderIndex}
              onChange={handleSliderChange}
              aria-label="Reasoning effort level"
              aria-valuetext={displayedOption.label}
              className="arrow-reasoning-slider"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReasoningEffortPicker;
